import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  IconEdit,
  IconTrash,
  IconPlus,
  IconRefresh,
  IconList,
} from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import moment from "moment";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const ListButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ContractTypesList = () => {
  const [contractTypes, setcontractTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentContractType, setcurrentContractType] = useState({
    code: "",
    name: "",
    active: true,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error' | 'warning' | 'info'
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContractType, setSelectedContractType] = useState(null);
  const [interestRates, setInterestRates] = useState([]);
  const [interestRatesModalOpen, setInterestRatesModalOpen] = useState(false);
  const [interestRatesPage, setInterestRatesPage] = useState(0);
  const [interestRatesRowsPerPage, setInterestRatesRowsPerPage] = useState(5);
  const [interestRatesTotal, setInterestRatesTotal] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});

  const fetchcontractTypes = useCallback(() => {
    setLoading(true);
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/contract-types/?page=${
        page + 1
      }&page_size=${rowsPerPage}${searchText}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Não foi possível carregar a lista de tipos de contrato. Tente novamente.");
        }
        return response.json();
      })
      .then((data) => {
        setcontractTypes(data.results);
        setTotalRecords(data.count);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchcontractTypes();
  }, [fetchcontractTypes, page, rowsPerPage, searchTerm]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (contractType) => {
    setcurrentContractType(contractType);
    setIsEditing(true);
    setOpenModal(true);
    setFieldErrors({});
  };

  const handleAdd = () => {
    setcurrentContractType({
      code: "",
      name: "",
      active: true,
    });
    setIsEditing(false);
    setOpenModal(true);
    setFieldErrors({});
  };

  const fetchInterestRates = (
    contractTypeId,
    page = interestRatesPage,
    rowsPerPage = interestRatesRowsPerPage
  ) => {
    fetch(
      `/backend/contract-type-interest-rate/?contract_type=${contractTypeId}&page=${
        page + 1
      }&page_size=${rowsPerPage}`
    )
      .then((res) => {
        if (!res.ok) throw new Error("Não foi possível carregar as taxas de juros. Tente novamente.");
        return res.json();
      })
      .then((data) => {
        setInterestRates(data.results);
        setInterestRatesTotal(data.count);
      })
      .catch((err) => {
        showSnackbar("Não foi possível carregar as taxas de juros. " + err.message, "error");
      });
  };

  const handleViewInterestRates = (contractType) => {
    setSelectedContractType(contractType);
    setInterestRates([]);
    setInterestRatesPage(0); // resetar página ao abrir
    setInterestRatesModalOpen(true);
    fetchInterestRates(contractType.id, 0, interestRatesRowsPerPage);
  };

  const handleInterestRatesPageChange = (event, newPage) => {
    setInterestRatesPage(newPage);
    fetchInterestRates(
      selectedContractType.id,
      newPage,
      interestRatesRowsPerPage
    );
  };

  const handleInterestRatesRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setInterestRatesRowsPerPage(newRowsPerPage);
    setInterestRatesPage(0);
    fetchInterestRates(selectedContractType.id, 0, newRowsPerPage);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "/backend/contract-types-refresh-interest-rate/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code: "all" }),
        }
      );

      if (!response.ok) {
        throw new Error("Não foi possível atualizar as taxas de juros. Tente novamente.");
      }

      showSnackbar("Taxas de juros atualizadas com sucesso!", "success");
    } catch (error) {
      console.error("Erro na atualização:", error);
      showSnackbar("Não foi possível atualizar as taxas de juros. " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFieldErrors({});
  };

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Deseja realmente fechar? Todas as alterações não salvas serão descartadas.")) {
      setOpenModal(false);
      setFieldErrors({});
    }
  };

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/contract-types/${currentContractType.id}/`
      : "/backend/contract-types/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentContractType),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        // Tenta extrair os erros específicos de cada campo do backend
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          // Mapeia os erros do backend para os campos do formulário
          const fieldMapping = {
            code: 'code',
            name: 'name',
            active: 'active'
          };
          
          // Processa erros de campos específicos
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          // Erros gerais
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          // Se houver erros de campos, atualiza o estado
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          // Monta mensagem de erro geral
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            throw new Error("Por favor, corrija os erros nos campos destacados.");
          } else {
            throw new Error("Não foi possível salvar o tipo de contrato. Verifique os dados e tente novamente.");
          }
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
          if (e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Não foi possível salvar o tipo de contrato. Verifique os dados e tente novamente.");
        }
      })
      .then((data) => {
        if (isEditing) {
          setcontractTypes((prevcontractTypes) =>
            prevcontractTypes.map((contractType) =>
              contractType.id === data.id ? data : contractType
            )
          );
        } else {
          setcontractTypes((prevcontractTypes) => [...prevcontractTypes, data]);
        }
        alert("Tipo de contrato cadastrado com sucesso!");
        setOpenModal(false);
        setFieldErrors({});
        fetchcontractTypes();
      })
      .catch((error) => alert("Não foi possível salvar o tipo de contrato. " + error.message));
  };

  const handleDelete = (contractTypeId) => {
    if (
      window.confirm(
        "Deseja realmente excluir este tipo de contrato? Esta ação não pode ser desfeita."
      )
    ) {
      fetch(`/backend/contract-types/${contractTypeId}/`, {
        method: "DELETE",
      })
        .then((response) => {
          if (response.ok) {
            setcontractTypes((prevcontractTypes) =>
              prevcontractTypes.filter(
                (contractType) => contractType.id !== contractTypeId
              )
            );
            alert("Tipo de contrato excluído com sucesso!");
            fetchcontractTypes();
          } else {
            throw new Error("Não foi possível excluir o tipo de contrato. Verifique se não há vínculos com outros registros.");
          }
        })
        .catch((error) => alert("Não foi possível excluir o tipo de contrato. " + error.message));
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de tipos de contrato"
        actions={[
          <AddButton onClick={handleRefresh}>
            <IconRefresh stroke={1.5} size="1.1rem" sx={{ mr: 1 }} />
            {loading ? "Atualizando..." : "Atualizar taxas"}
          </AddButton>,
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar tipo
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : contractTypes.length > 0 ? (
              contractTypes.map((contractType) => (
                <TableRow key={contractType.id}>
                  <TableCell>{contractType.code}</TableCell>
                  <TableCell>{contractType.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={contractType.active ? "Ativo" : "Inativo"} 
                      color={contractType.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver taxas de juros">
                      <ListButton
                        onClick={() => handleViewInterestRates(contractType)}
                      >
                        <IconList stroke={1.5} size="1.3rem" />
                      </ListButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <EditButton onClick={() => handleEdit(contractType)}>
                        <IconEdit stroke={1.5} size="1.3rem" />
                      </EditButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <DeleteButton
                        onClick={() => handleDelete(contractType.id)}
                      >
                        <IconTrash stroke={1.5} size="1.3rem" />
                      </DeleteButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nenhum tipo de contrato cadastrado.
                  </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Tipos de contrato por página"
      />

      <Dialog 
        open={openModal} 
        onClose={(event, reason) => {
          // Impede o fechamento do modal ao clicar fora
          if (reason === 'backdropClick') {
            return;
          }
          // Para tecla ESC, solicita confirmação
          if (reason === 'escapeKeyDown') {
            handleCloseWithConfirmation();
            return;
          }
          handleCloseModal();
        }}
      >
        <DialogTitle>
          {isEditing ? "Editar Tipo de Contrato" : "Adicionar Tipo de Contrato"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Código"
            value={currentContractType.code}
            onChange={(e) => {
              setcurrentContractType({
                ...currentContractType,
                code: e.target.value,
              });
              // Limpa o erro do campo quando o usuário começa a digitar
              if (fieldErrors.code) {
                setFieldErrors({ ...fieldErrors, code: null });
              }
            }}
            fullWidth
            inputProps={{ maxLength: 6 }}
            sx={{ mt: 2 }}
            autoComplete="new-password"
            error={!!fieldErrors.code}
            helperText={fieldErrors.code || ""}
          />

          <TextField
            label="Nome"
            value={currentContractType.name}
            onChange={(e) => {
              setcurrentContractType({
                ...currentContractType,
                name: e.target.value,
              });
              // Limpa o erro do campo quando o usuário começa a digitar
              if (fieldErrors.name) {
                setFieldErrors({ ...fieldErrors, name: null });
              }
            }}
            fullWidth
            sx={{ mt: 2 }}
            autoComplete="new-password"
            error={!!fieldErrors.name}
            helperText={fieldErrors.name || ""}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={currentContractType.active || false}
                onChange={(e) =>
                  setcurrentContractType({
                    ...currentContractType,
                    active: e.target.checked,
                  })
                }
                color="primary"
              />
            }
            label="Ativo (aparece no cadastro de contratos)"
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={interestRatesModalOpen}
        onClose={() => setInterestRatesModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Taxas de Juros - {selectedContractType?.name}</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {interestRates.length > 0 ? (
                interestRates.map((rate, index) => (
                  <TableRow key={rate.id || index}>
                    <TableCell>
                      {moment(rate.date).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat("pt-BR", {
                        style: "percent",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(rate.value / 100)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nenhuma taxa de juros cadastrada para este tipo de contrato.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={interestRatesTotal}
            rowsPerPage={interestRatesRowsPerPage}
            page={interestRatesPage}
            onPageChange={handleInterestRatesPageChange}
            onRowsPerPageChange={handleInterestRatesRowsPerPageChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterestRatesModalOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContractTypesList;
